import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { FC_RANK, REVENUE_DISCLAIMER } from "@/lib/ideas";

const ROWS = [["startup_cost", "Startup cost"], ["difficulty", "Difficulty"], ["time_required", "Time required"], ["margin", "Potential margin"],
  ["first_customer_difficulty", "First customer difficulty"], ["competition", "Competition"], ["revenue_monthly_text", "Estimated monthly revenue"],
  ["revenue_annual_text", "Estimated annual revenue"], ["why_customers_buy", "Why customers buy"], ["target", "Target customer"]];

function highlights(ideas) {
  const h = {};
  const add = (n, l) => { h[n] = [...(h[n] || []), l]; };
  const minBy = (f) => Math.min(...ideas.map(f));
  const maxBy = (f) => Math.max(...ideas.map(f));
  const minCost = minBy((i) => i.startup_low ?? Infinity);
  const minFc = minBy((i) => FC_RANK[i.first_customer_difficulty] ?? 9);
  const maxRev = maxBy((i) => i.estimated_monthly_revenue_high ?? 0);
  ideas.forEach((i) => {
    if (i.startup_low === minCost) add(i.number, "Lowest startup cost");
    if ((FC_RANK[i.first_customer_difficulty] ?? 9) === minFc) add(i.number, "Easiest first customer");
    if (maxRev && i.estimated_monthly_revenue_high === maxRev) add(i.number, "Highest revenue potential");
    if (i.difficulty === "Beginner") add(i.number, "Beginner friendly");
  });
  return h;
}

export default function CompareIdeas() {
  const [params] = useSearchParams();
  const [ideas, setIdeas] = useState(null);
  useEffect(() => {
    const ids = (params.get("ids") || "").split(",").filter(Boolean).slice(0, 3);
    Promise.all(ids.map((n) => api.get(`/ideas/${n}`).then((r) => r.data))).then(setIdeas);
  }, [params]);
  if (!ideas) return <Loader />;
  const hl = highlights(ideas);
  return (
    <div>
      <Link to="/ideas" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73] mb-4" data-testid="compare-back-link"><ArrowLeft className="h-4 w-4" /> Back to ideas</Link>
      <PageHeader eyebrow="Compare" title="Side-by-side comparison" subtitle="Highlights help you spot differences — you decide which fits you best." />
      <div className="overflow-x-auto -mx-4 px-4 pb-4">
        <table className="min-w-[680px] w-full border-separate border-spacing-0 sb-card overflow-hidden" data-testid="compare-table">
          <thead>
            <tr>
              <th className="w-40 bg-[#F5F0E6] p-4" />
              {ideas.map((i) => (
                <th key={i.number} className="p-4 text-left align-top bg-white border-l border-[#EADEC9]" data-testid={`compare-col-${i.number}`}>
                  <Link to={`/ideas/${i.number}`} className="font-display font-bold text-base hover:text-[#179B80]">{i.name}</Link>
                  <div className="text-[11px] text-[#8C9BA5] font-medium">{i.category}</div>
                  <div className="flex flex-wrap gap-1 mt-2">{(hl[i.number] || []).map((l) => <span key={l} className="sb-chip bg-[#E8F8F4] text-[#179B80]" data-testid={`compare-highlight-${i.number}`}>{l}</span>)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([k, l]) => (
              <tr key={k}>
                <td className="bg-[#F5F0E6] p-4 text-xs font-bold uppercase tracking-wider text-[#5C6B73] border-t border-[#EADEC9] align-top">{l}</td>
                {ideas.map((i) => <td key={i.number} className={`p-4 text-sm border-t border-l border-[#EADEC9] align-top ${k.startsWith("revenue") ? "font-semibold" : ""}`}>{i[k] || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#8C9BA5] mt-2 max-w-2xl" data-testid="compare-disclaimer">{REVENUE_DISCLAIMER}</p>
    </div>
  );
}
